import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from './LanguageWrapper';

const LanguageSwitcher = ({ className = '', variant = 'globe' }) => {
    const { i18n } = useTranslation('common');
    const navigate = useNavigate();
    const location = useLocation();
    const { lang } = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLang = lang || i18n.language || 'en';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const switchLanguage = (newLang) => {
        if (newLang === currentLang) {
            setIsOpen(false);
            return;
        }

        // Replace the language prefix in the current path
        const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
        const newPath = `/${newLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;

        navigate(newPath);
        setIsOpen(false);
    };

    const languageNames = {
        en: 'English',
        da: 'Dansk'
    };

    // Globe dropdown variant (default)
    if (variant === 'globe') {
        return (
            <div className={`relative ${className}`} ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all duration-200"
                    aria-label="Select language"
                    aria-expanded={isOpen}
                >
                    {/* Globe Icon */}
                    <svg
                        className="w-5 h-5 opacity-70"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                        />
                    </svg>
                    <span className="text-sm font-medium uppercase">{currentLang}</span>
                    {/* Chevron */}
                    <svg
                        className={`w-3 h-3 opacity-50 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 min-w-[140px]">
                        {SUPPORTED_LANGUAGES.map((langCode) => (
                            <button
                                key={langCode}
                                onClick={() => switchLanguage(langCode)}
                                className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150 flex items-center justify-between ${currentLang === langCode
                                    ? 'bg-emerald-50 text-emerald-600'
                                    : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                <span>{languageNames[langCode]}</span>
                                {currentLang === langCode && (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Toggle variant - just show the other language as a clickable option
    if (variant === 'toggle') {
        const otherLang = currentLang === 'en' ? 'da' : 'en';
        return (
            <button
                onClick={() => switchLanguage(otherLang)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80 ${className}`}
                aria-label={`Switch to ${languageNames[otherLang]}`}
            >
                <span className="uppercase">{otherLang}</span>
            </button>
        );
    }

    // Default buttons variant
    return (
        <div className={`flex items-center gap-1 ${className}`}>
            {SUPPORTED_LANGUAGES.map((langCode) => (
                <button
                    key={langCode}
                    onClick={() => switchLanguage(langCode)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${currentLang === langCode
                        ? 'bg-emerald-500 text-white'
                        : 'hover:bg-gray-100 text-gray-600 dark:hover:bg-gray-800 dark:text-gray-300'
                        }`}
                    aria-label={languageNames[langCode]}
                    aria-current={currentLang === langCode ? 'true' : undefined}
                >
                    {langCode.toUpperCase()}
                </button>
            ))}
        </div>
    );
};

export default LanguageSwitcher;
